import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import Loader from '../../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../../services/ApiService';
import { formatDate } from '../../../utils/helper.js';
import Paginator from '../../../components/Paginator';
import Swal from 'sweetalert2';
import './Details.css';

const MAROON = '#c4941a';
const VALID_TABS = ['info', 'bookings', 'services', 'reviews', 'timings', 'bank'];
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DEFAULT_TIMINGS = DAYS.map(day => ({ day, is_off: false, open: '09:00', close: '18:00' }));

const TABS = [
  { key: 'info',     label: 'Business Info',     icon: 'bi-building'         },
  { key: 'timings',  label: 'Business Timings',            icon: 'bi-clock'            },
  { key: 'services', label: 'Services',           icon: 'bi-grid'             },
  { key: 'bookings', label: 'Booking History',    icon: 'bi-calendar-check'   },
  { key: 'reviews',  label: 'Reviews',            icon: 'bi-star'             },
  { key: 'bank',     label: 'Bank Details',       icon: 'bi-bank'             },
];

const EMPTY_BANK = { account_title: '', account_number: '', iban: '', bank_name: '', city: '' };

const DOCUMENTS = [
  { key: 'business_license',        label: 'Business License' },
  { key: 'emirates_id',             label: 'Emirates ID' },
  { key: 'trade_name_certificate',  label: 'Trade Name Certificate' },
];

const PartnerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'info';
  const [isLoader, setIsLoader]   = useState(false);
  const [tab, setTab]             = useState(initialTab);
  const [partner, setpartner]     = useState({});
  const [documents, setDocuments] = useState({});

  // Services state
  const [services, setServices]           = useState([]);
  const [servicesPagination, setServicesPagination] = useState({});
  const [servicesPage, setServicesPage]   = useState(1);
  const [servicesSearch, setServicesSearch] = useState('');
  const [servicesLoader, setServicesLoader] = useState(false);

  // Bookings state
  const [bookings, setBookings]           = useState([]);
  const [bookingsPagination, setBookingsPagination] = useState({});
  const [bookingsPage, setBookingsPage]   = useState(1);
  const [bookingsLoader, setBookingsLoader] = useState(false);

  // Reviews state
  const [reviews, setReviews]               = useState([]);
  const [reviewsPagination, setReviewsPagination] = useState({});
  const [reviewsPage, setReviewsPage]       = useState(1);
  const [reviewsLoader, setReviewsLoader]   = useState(false);
  const [reviewsAvg, setReviewsAvg]         = useState(0);
  const [reviewsTotal, setReviewsTotal]     = useState(0);

  // Timings state
  const [timings, setTimings]             = useState(DEFAULT_TIMINGS);
  const [slotDuration, setSlotDuration]   = useState(1);
  const [timingsLoader, setTimingsLoader] = useState(false);
  const [timingsSaving, setTimingsSaving] = useState(false);
  const [applyAllOpen, setApplyAllOpen]   = useState('09:00');
  const [applyAllClose, setApplyAllClose] = useState('18:00');

  // Bank details state
  const [bank, setBank]             = useState(EMPTY_BANK);
  const [bankLoader, setBankLoader] = useState(false);
  const [bankSaving, setBankSaving] = useState(false);

  const switchTab = (key) => {
    setTab(key);
    setSearchParams({ tab: key }, { replace: true });
  };

  useEffect(() => { if (id) { getSingleData(); getDocuments(); } }, [id]);

  useEffect(() => {
    if (tab === 'services') getServices();
  }, [tab, servicesPage, servicesSearch]);

  useEffect(() => {
    if (tab === 'bookings') getBookings();
  }, [tab, bookingsPage]);

  useEffect(() => {
    if (tab === 'reviews') getReviews();
  }, [tab, reviewsPage]);

  useEffect(() => {
    if (tab === 'timings') getTimings();
  }, [tab]);

  useEffect(() => {
    if (tab === 'bank') getBankDetails();
  }, [tab]);

  const getSingleData = async () => {
    try {
      setIsLoader(true);
      const res = await ApiService.request({ method: 'GET', url: `admin/partners/${id}` });
      if (res.data.status) setpartner(res.data.data);
      else toast.error(res.data.message);
    } catch (_) {}
    finally { setIsLoader(false); }
  };

  const getDocuments = async () => {
    try {
      const res = await ApiService.request({ method: 'GET', url: `admin/partners/${id}/documents` });
      if (res.data.status && res.data.data) setDocuments(res.data.data);
    } catch (_) {}
  };

  const getServices = async () => {
    try {
      setServicesLoader(true);
      const params = new URLSearchParams({ page: servicesPage, limit: 15, ...(servicesSearch && { search: servicesSearch }) }).toString();
      const res = await ApiService.request({ method: 'GET', url: `admin/partners/${id}/services?${params}` });
      if (res.data.status) { setServices(res.data.data); setServicesPagination(res.data.pagination); }
      else toast.error(res.data.message);
    } catch (_) {}
    finally { setServicesLoader(false); }
  };

  const getBookings = async () => {
    try {
      setBookingsLoader(true);
      const params = new URLSearchParams({ partner_id: id, page: bookingsPage, limit: 15 }).toString();
      const res = await ApiService.request({ method: 'GET', url: `admin/bookings?${params}` });
      if (res.data.status) { setBookings(res.data.data); setBookingsPagination(res.data.pagination); }
      else toast.error(res.data.message);
    } catch (_) {}
    finally { setBookingsLoader(false); }
  };

  const getReviews = async () => {
    try {
      setReviewsLoader(true);
      const params = new URLSearchParams({ page: reviewsPage, limit: 10 }).toString();
      const res = await ApiService.request({ method: 'GET', url: `admin/partners/${id}/reviews?${params}` });
      if (res.data.status) {
        setReviews(res.data.data);
        setReviewsPagination(res.data.pagination);
        setReviewsAvg(res.data.avg_stars || 0);
        setReviewsTotal(res.data.total_reviews || 0);
      } else toast.error(res.data.message);
    } catch (_) {}
    finally { setReviewsLoader(false); }
  };

  const getTimings = async () => {
    try {
      setTimingsLoader(true);
      const res = await ApiService.request({ method: 'GET', url: `admin/partners/${id}/timings` });
      if (res.data.status && res.data.data) {
        const { timings: apiTimings, slot_duration } = res.data.data;
        if (apiTimings?.length) setTimings(apiTimings);
        if (slot_duration) setSlotDuration(slot_duration);
      }
    } catch (_) {}
    finally { setTimingsLoader(false); }
  };

  const saveTimings = async () => {
    try {
      setTimingsSaving(true);
      const res = await ApiService.request({
        method: 'PUT',
        url: `admin/partners/${id}/timings`,
        data: { timings, slot_duration: slotDuration },
      });
      if (res.data.status) toast.success(res.data.message || 'Timings saved successfully');
      else toast.error(res.data.message);
    } catch (_) { toast.error('Failed to save timings'); }
    finally { setTimingsSaving(false); }
  };

  const getBankDetails = async () => {
    try {
      setBankLoader(true);
      const res = await ApiService.request({ method: 'GET', url: `admin/partners/${id}/bank-details` });
      if (res.data.status && res.data.data?.bank_details) {
        const b = res.data.data.bank_details;
        setBank({
          account_title:  b.account_title || '',
          account_number: b.account_number || '',
          iban:           b.iban || '',
          bank_name:      b.bank_name || '',
          city:           b.city || '',
        });
      }
    } catch (_) {}
    finally { setBankLoader(false); }
  };

  const saveBankDetails = async () => {
    try {
      setBankSaving(true);
      const res = await ApiService.request({
        method: 'PUT',
        url: `admin/partners/${id}/bank-details`,
        data: bank,
      });
      if (res.data.status) toast.success(res.data.message || 'Bank details saved successfully');
      else toast.error(res.data.message);
    } catch (_) { toast.error('Failed to save bank details'); }
    finally { setBankSaving(false); }
  };

  const updateBankField = (field, value) => {
    setBank(prev => ({ ...prev, [field]: value }));
  };

  const updateDay = (index, field, value) => {
    setTimings(prev => prev.map((t, i) => i === index ? { ...t, [field]: value } : t));
  };

  const applyAllTimings = () => {
    setTimings(prev => prev.map(t => t.is_off ? t : { ...t, open: applyAllOpen, close: applyAllClose }));
  };

  const handleApprove = () => {
    Swal.fire({
      title: 'Approve partner',
      text: 'Are you sure you want to approve this partner?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, approve!',
      confirmButtonColor: MAROON,
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        setIsLoader(true);
        const res = await ApiService.request({ method: 'POST', url: `admin/partners/approval-status/${id}`, data: { status: 'Approved' } });
        if (res.data.status) { toast.success(res.data.message); getSingleData(); }
        else toast.error(res.data.message);
      } catch (_) { toast.error('Error approving partner'); }
      finally { setIsLoader(false); }
    });
  };

  const handleReject = () => {
    Swal.fire({
      title: 'Reject partner',
      html: `<p>Are you sure you want to reject this partner?</p><input id="swal-reason" class="swal2-input" placeholder="Reason for rejection">`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, reject!',
      confirmButtonColor: '#d33',
      preConfirm: () => {
        const reason = Swal.getPopup().querySelector('#swal-reason').value;
        if (!reason) { Swal.showValidationMessage('Please enter a reason'); return false; }
        return reason;
      },
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        setIsLoader(true);
        const res = await ApiService.request({ method: 'POST', url: `admin/partners/approval-status/${id}`, data: { status: 'Rejected', reason: result.value } });
        if (res.data.status) { toast.success(res.data.message); getSingleData(); }
        else toast.error(res.data.message);
      } catch (_) { toast.error('Error rejecting partner'); }
      finally { setIsLoader(false); }
    });
  };

  const handleTogglepartnerStatus = async () => {
    const next = partner.status ? 'Inactive' : 'Active';
    const result = await Swal.fire({
      title: `Set as ${next}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: MAROON,
      confirmButtonText: `Yes, set ${next}`,
    });
    if (!result.isConfirmed) return;
    try {
      setIsLoader(true);
      const res = await ApiService.request({ method: 'POST', url: `admin/partners/status/${id}` });
      if (res.data.status) { toast.success(res.data.message); getSingleData(); }
      else toast.error(res.data.message);
    } catch (_) { toast.error('Failed to update status'); }
    finally { setIsLoader(false); }
  };

  const handleDeleteService = (svcId) => {
    Swal.fire({
      title: 'Delete Service', text: 'This cannot be undone.', icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#d33',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const res = await ApiService.request({ method: 'DELETE', url: `admin/services/${svcId}` });
        if (res.data.status) { toast.success(res.data.message); getServices(); }
        else toast.error(res.data.message);
      } catch (_) { toast.error('Error deleting service'); }
    });
  };

  const handleToggleStatus = async (svcId, currentStatus) => {
    const next = currentStatus ? 'Inactive' : 'Active';
    const result = await Swal.fire({
      title: `Set service as ${next}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: MAROON,
      confirmButtonText: `Yes, set ${next}`,
    });
    if (!result.isConfirmed) return;
    try {
      const res = await ApiService.request({ method: 'POST', url: `admin/services/status/${svcId}` });
      if (res.data.status) getServices();
      else toast.error(res.data.message);
    } catch (_) {}
  };

  const approvalBadgeClass = (s) => s === 'Approved' ? 'detail-badge-approved' : s === 'Rejected' ? 'detail-badge-rejected' : 'detail-badge-pending';

  const bookingStatusColor = (s) => {
    const map = { pending: '#FFC107', confirmed: '#2563EB', completed: '#059669', cancelled: '#DC2626' };
    return map[s?.toLowerCase()] || '#9CA3AF';
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      {/* Breadcrumb toolbar */}
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              {partner?.company_name || 'partner Details'}
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Dashboard</Link>
              </li>
              <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/partners" className="text-muted text-hover-primary">Providers</Link>
              </li>
              <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
              <li className="breadcrumb-item text-muted">Details</li>
            </ul>
          </div>
          <div className="d-flex align-items-center gap-2 gap-lg-3">
            {/* Pending: Approve + Reject */}
            {partner?.approval_status === 'Pending' && <>
              <button onClick={handleApprove} className="btn btn-sm btn-success">
                <i className="bi bi-check-circle-fill me-2"></i>Approve
              </button>
              <button onClick={handleReject} className="btn btn-sm btn-danger">
                <i className="bi bi-x-circle-fill me-2"></i>Reject
              </button>
            </>}
            {/* Rejected: re-approve only */}
            {partner?.approval_status === 'Rejected' && (
              <button onClick={handleApprove} className="btn btn-sm btn-success">
                <i className="bi bi-check-circle-fill me-2"></i>Approve
              </button>
            )}
            {/* Approved: Active / Inactive toggle */}
            {partner?.approval_status === 'Approved' && (
              <button
                onClick={handleTogglepartnerStatus}
                className={`btn btn-sm ${partner.status ? 'btn-danger' : 'btn-success'}`}
              >
                <i className={`bi ${partner.status ? 'bi-slash-circle-fill' : 'bi-check-circle-fill'} me-2`}></i>
                {partner.status ? 'Set Inactive' : 'Set Active'}
              </button>
            )}
            <Link to="/admin/partners" className="btn btn-sm btn-light">
              <i className="bi bi-arrow-left me-2"></i>Back
            </Link>
          </div>
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">

          {isLoader && !partner?._id ? (
            <div className="text-center py-10"><Loader text="Loading…" size={35} /></div>
          ) : (
            <div className="partner-detail-layout">

              {/* ── Left Sidebar ── */}
              <div className="partner-detail-sidebar">
                {/* Profile card */}
                <div className="partner-sidebar-profile">
                  <div className="partner-sidebar-cover">
                    <div className="partner-sidebar-logo">
                      {partner.logo ? (
                        <img src={partner.logo} alt={partner.company_name} />
                      ) : (
                        <div className="partner-sidebar-logo-placeholder">
                          <i className="bi bi-building-fill text-white" style={{ fontSize: 26 }}></i>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="partner-sidebar-info">
                    <div className="partner-sidebar-name">{partner.company_name || 'N/A'}</div>
                    <div className="partner-sidebar-category">
                      {partner.categories?.map((c) => c?.title || c).join(', ') || 'No categories'}
                    </div>
                    <span className={approvalBadgeClass(partner.approval_status)}>
                      {partner.approval_status || 'Pending'}
                    </span>
                  </div>
                </div>

                {/* Tab nav */}
                <div className="partner-sidebar-nav">
                  {TABS.map(({ key, label, icon }) => (
                    <button
                      key={key}
                      className={`partner-sidebar-nav-item ${tab === key ? 'active' : ''}`}
                      onClick={() => switchTab(key)}
                    >
                      <i className={`bi ${icon}`}></i>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Right Content ── */}
              <div className="partner-detail-content">

                {/* ── Business Info ── */}
                {tab === 'info' && (
                  <div>
                    <div className="partner-info-card">
                      <div className="partner-info-card-header">
                        <h6 className="partner-info-card-title">
                          <i className="bi bi-info-circle-fill"></i>Basic Information
                        </h6>
                        <Link to={`/admin/partners/create/${id}`} className="btn btn-sm btn-light" style={{ fontSize: 12 }}>
                          <i className="bi bi-pencil-fill me-1"></i>Edit
                        </Link>
                      </div>
                      <div className="partner-info-card-body">
                        {[
                          { label: 'partner Name',  value: partner.company_name },
                          { label: 'Owner Name',    value: partner.owner_name || '—' },
                          { label: 'Email',         value: partner.email },
                          { label: 'Phone',         value: partner.phone ? `${partner.phone_code ? '+' + partner.phone_code + ' ' : ''}${partner.phone}` : '—' },
                          { label: 'Website',       value: partner.website ? <a href={partner.website} target="_blank" rel="noreferrer" style={{ color: MAROON }}>{partner.website}</a> : '—' },
                          { label: 'Address',       value: partner.address || '—' },
                          { label: 'Order Accept Type', value: partner.order_accept_type || 'Manual' },
                          { label: 'Registered',    value: formatDate(partner.createdAt, 'DD MMM YYYY, hh:mm A') },
                        ].map(({ label, value }) => (
                          <div key={label} className="partner-info-row">
                            <span className="partner-info-label">{label}</span>
                            <span className="partner-info-value">{value}</span>
                          </div>
                        ))}
                        <div className="partner-info-row">
                          <span className="partner-info-label">Approval</span>
                          <span className="partner-info-value">
                            <span className={approvalBadgeClass(partner.approval_status)}>
                              {partner.approval_status || 'Pending'}
                            </span>
                          </span>
                        </div>
                        <div className="partner-info-row">
                          <span className="partner-info-label">Status</span>
                          <span className="partner-info-value">
                            <span className={partner.status ? 'detail-badge-active' : 'detail-badge-inactive'}>
                              {partner.status ? 'Active' : 'Inactive'}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Location */}
                    {partner.address && (
                      <div className="partner-info-card">
                        <div className="partner-info-card-header">
                          <h6 className="partner-info-card-title">
                            <i className="bi bi-geo-alt-fill"></i>Location
                          </h6>
                        </div>
                        <div className="partner-location-card">
                          <div className="partner-location-map-placeholder">
                            <i className="bi bi-map"></i>
                            <span>Map view</span>
                          </div>
                          <div className="partner-location-address">
                            <i className="bi bi-geo-alt-fill"></i>
                            <span>{partner.address}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* About */}
                    {partner.about && (
                      <div className="partner-info-card">
                        <div className="partner-info-card-header">
                          <h6 className="partner-info-card-title">
                            <i className="bi bi-file-text-fill"></i>About
                          </h6>
                        </div>
                        <div style={{ padding: '16px 20px', fontSize: 14, color: '#4b5563', lineHeight: 1.7 }}>
                          {partner.about}
                        </div>
                      </div>
                    )}

                    {/* Categories */}
                    {partner.categories?.length > 0 && (
                      <div className="partner-info-card">
                        <div className="partner-info-card-header">
                          <h6 className="partner-info-card-title">
                            <i className="bi bi-tags-fill"></i>Categories
                          </h6>
                        </div>
                        <div className="partner-category-chips">
                          {partner.categories.map((cat, i) => (
                            <span key={i} className="partner-category-chip">{cat?.title || cat}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Served Emirates */}
                    {partner.emirates?.length > 0 && (
                      <div className="partner-info-card">
                        <div className="partner-info-card-header">
                          <h6 className="partner-info-card-title">
                            <i className="bi bi-geo-alt-fill"></i>Served Emirates
                          </h6>
                        </div>
                        <div className="partner-category-chips">
                          {partner.emirates.map((em, i) => (
                            <span key={i} className="partner-category-chip">{em}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Cover images */}
                    {partner.cover_images?.length > 0 && (
                      <div className="partner-info-card">
                        <div className="partner-info-card-header">
                          <h6 className="partner-info-card-title">
                            <i className="bi bi-images"></i>Cover Images
                          </h6>
                        </div>
                        <div className="partner-images-grid">
                          {partner.cover_images.map((img, i) => (
                            <img key={i} src={img} alt="cover" className="partner-image-thumb" />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Documents */}
                    {DOCUMENTS.some((d) => documents[d.key]) && (
                      <div className="partner-info-card">
                        <div className="partner-info-card-header">
                          <h6 className="partner-info-card-title">
                            <i className="bi bi-file-earmark-text-fill"></i>Documents
                          </h6>
                        </div>
                        <div className="partner-documents-grid">
                          {DOCUMENTS.filter((d) => documents[d.key]).map((d) => {
                            const url = documents[d.key];
                            const isImage = typeof url === 'string' && /\.(jpeg|jpg|gif|png|webp)(\?|$)/i.test(url);
                            return (
                              <a
                                key={d.key}
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                className="partner-document-card"
                                title={`View ${d.label}`}
                              >
                                <div className="partner-document-preview">
                                  {isImage ? (
                                    <img src={url} alt={d.label} />
                                  ) : (
                                    <i className="bi bi-file-earmark-pdf-fill"></i>
                                  )}
                                </div>
                                <span className="partner-document-label">{d.label}</span>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Booking History ── */}
                {tab === 'bookings' && (
                  <div className="partner-info-card">
                    <div className="partner-info-card-header">
                      <h6 className="partner-info-card-title">
                        <i className="bi bi-calendar-check-fill"></i>Booking History
                      </h6>
                    </div>
                    {bookingsLoader ? (
                      <div className="text-center py-8"><Loader text="Loading…" size={30} /></div>
                    ) : bookings.length === 0 ? (
                      <div className="partner-tab-empty">
                        <i className="bi bi-calendar-x"></i>
                        <div>No bookings found for this provider</div>
                      </div>
                    ) : (
                      <>
                        <div>
                          {bookings.map((bk) => (
                            <div
                              key={bk._id}
                              className="partner-booking-row"
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate(`/admin/bookings?booking=${bk._id}`)}
                            >
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, fontSize: 14, color: '#2d3748' }}>
                                  {bk.booking_code || `#${bk._id?.toString().slice(-6)}`}
                                </div>
                                <div style={{ fontSize: 12, color: '#6c757d' }}>
                                  {bk.user_id?.name || 'N/A'}
                                </div>
                              </div>
                              <div style={{ textAlign: 'center', fontSize: 13, color: '#4b5563' }}>
                                {bk.event_date ? new Date(bk.event_date).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                                {bk.event_time && <span className="ms-1 text-muted">{bk.event_time}</span>}
                              </div>
                              <div style={{ minWidth: 80, textAlign: 'right', fontWeight: 600, fontSize: 14, color: MAROON }}>
                                AED {bk.grand_total ?? bk.total_price ?? 0}
                              </div>
                              <div style={{ minWidth: 90, textAlign: 'right' }}>
                                <span style={{
                                  fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                                  background: bookingStatusColor(bk.status) + '20',
                                  color: bookingStatusColor(bk.status),
                                  border: `1px solid ${bookingStatusColor(bk.status)}40`,
                                  textTransform: 'capitalize',
                                }}>
                                  {bk.status || 'N/A'}
                                </span>
                              </div>
                              <div style={{ minWidth: 24 }}>
                                <i className="bi bi-chevron-right text-muted" style={{ fontSize: 12 }}></i>
                              </div>
                            </div>
                          ))}
                        </div>
                        {bookingsPagination.total_pages > 1 && (
                          <div className="d-flex justify-content-between align-items-center px-4 py-3 border-top">
                            <span className="text-muted fs-7">
                              {bookings.length} of {bookingsPagination.total_records} bookings
                            </span>
                            <Paginator
                              page={bookingsPagination.current_page}
                              rows={bookingsPagination.per_page}
                              totalRecords={bookingsPagination.total_records}
                              onPageChange={(p) => setBookingsPage(p)}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* ── Services ── */}
                {tab === 'services' && (
                  <div className="partner-info-card">
                    <div className="partner-info-card-header">
                      <h6 className="partner-info-card-title">
                        <i className="bi bi-grid-fill"></i>Services
                      </h6>
                      <div className="d-flex align-items-center gap-2">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Search services…"
                          style={{ width: 180 }}
                          value={servicesSearch}
                          onChange={(e) => { setServicesSearch(e.target.value); setServicesPage(1); }}
                        />
                        <button
                          className="btn btn-sm text-white btn-base"
                          style={{ whiteSpace: 'nowrap' }}
                          onClick={() => navigate(`/admin/partners/${id}/services/create`)}
                        >
                          <i className="bi bi-plus-lg me-1"></i>Add
                        </button>
                      </div>
                    </div>
                    {servicesLoader ? (
                      <div className="text-center py-8"><Loader text="Loading…" size={30} /></div>
                    ) : services.length === 0 ? (
                      <div className="partner-tab-empty">
                        <i className="bi bi-grid-3x3"></i>
                        <div>No services found</div>
                      </div>
                    ) : (
                      <>
                        <div className="table-responsive">
                          <table className="table align-middle table-hover mb-0">
                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                              <tr className="text-start text-muted fw-bold fs-7 text-uppercase">
                                <th style={{ paddingLeft: 20 }}>Image</th>
                                <th>Title</th>
                                <th>Price</th>
                                <th>Status</th>
                                <th style={{ paddingRight: 20 }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody className="fw-semibold text-gray-600">
                              {services.map((svc) => (
                                <tr key={svc._id}>
                                  <td style={{ paddingLeft: 20 }}>
                                    {svc.image
                                      ? <img src={svc.image} alt={svc.title} className="rounded" style={{ width: 44, height: 44, objectFit: 'cover' }} />
                                      : <div className="rounded bg-light d-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}><i className="bi bi-image text-muted"></i></div>
                                    }
                                  </td>
                                  <td className="fw-bold text-dark">{svc.title}</td>
                                  <td>
                                    {svc.price != null ? (
                                      <span className="fw-semibold text-dark">AED {svc.price} <span className="text-muted fw-normal">/ {svc.price_type === 'per_hour' ? 'hour' : 'day'}</span></span>
                                    ) : (
                                      <span className="text-muted">—</span>
                                    )}
                                  </td>
                                  <td>
                                    <span
                                      className={`badge bg-${svc.status ? 'success' : 'secondary'}`}
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => handleToggleStatus(svc._id, svc.status)}
                                    >
                                      {svc.status ? 'Active' : 'Inactive'}
                                    </span>
                                  </td>
                                  <td style={{ paddingRight: 20 }}>
                                    <button
                                      className="btn btn-icon btn-light-primary btn-sm me-1"
                                      onClick={() => navigate(`/admin/partners/${id}/services/create/${svc._id}`)}
                                      title="Edit"
                                    >
                                      <i className="bi bi-pencil-fill fs-6"></i>
                                    </button>
                                    <button
                                      className="btn btn-icon btn-light-danger btn-sm"
                                      onClick={() => handleDeleteService(svc._id)}
                                      title="Delete"
                                    >
                                      <i className="bi bi-trash-fill fs-6"></i>
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {servicesPagination.total_pages > 1 && (
                          <div className="d-flex justify-content-between align-items-center px-4 py-3 border-top">
                            <span className="text-muted fs-7">
                              {services.length} of {servicesPagination.total_records} services
                            </span>
                            <Paginator
                              page={servicesPagination.current_page}
                              rows={servicesPagination.per_page}
                              totalRecords={servicesPagination.total_records}
                              onPageChange={(p) => setServicesPage(p)}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* ── Reviews ── */}
                {tab === 'reviews' && (
                  <div className="partner-info-card">
                    <div className="partner-info-card-header">
                      <h6 className="partner-info-card-title">
                        <i className="bi bi-star-fill"></i>Reviews
                      </h6>
                      {reviewsTotal > 0 && (
                        <div className="d-flex align-items-center gap-3">
                          <div className="review-avg-badge">
                            <i className="bi bi-star-fill" style={{ color: '#f59e0b', fontSize: 13 }}></i>
                            <span style={{ fontWeight: 700, fontSize: 15 }}>{reviewsAvg}</span>
                            <span style={{ fontSize: 12, color: '#6c757d' }}>/ 5</span>
                          </div>
                          <span style={{ fontSize: 12, color: '#6c757d' }}>{reviewsTotal} review{reviewsTotal !== 1 ? 's' : ''}</span>
                        </div>
                      )}
                    </div>

                    {reviewsLoader ? (
                      <div className="text-center py-8"><Loader text="Loading…" size={30} /></div>
                    ) : reviews.length === 0 ? (
                      <div className="partner-tab-empty">
                        <i className="bi bi-star"></i>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>No Reviews Yet</div>
                        <div style={{ fontSize: 13 }}>Customer reviews will appear here.</div>
                      </div>
                    ) : (
                      <>
                        <div>
                          {reviews.map((rv) => (
                            <div key={rv._id} className="review-row">
                              <div className="review-avatar">
                                {rv.user_id?.picture
                                  ? <img src={rv.user_id.picture} alt={rv.user_id?.name} className="review-avatar-img" />
                                  : <div className="review-avatar-placeholder">
                                      <i className="bi bi-person-fill"></i>
                                    </div>
                                }
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                                  <div>
                                    <span className="review-user-name">{rv.user_id?.name || 'Anonymous'}</span>
                                    <div className="review-stars">
                                      {[1,2,3,4,5].map(s => (
                                        <i key={s} className={`bi bi-star${s <= rv.stars ? '-fill' : ''}`} style={{ color: s <= rv.stars ? '#f59e0b' : '#d1d5db', fontSize: 13 }}></i>
                                      ))}
                                    </div>
                                  </div>
                                  <span className="review-date">{new Date(rv.createdAt).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                                </div>
                                {rv.review && <p className="review-text">{rv.review}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                        {reviewsPagination.total_pages > 1 && (
                          <div className="d-flex justify-content-between align-items-center px-4 py-3 border-top">
                            <span className="text-muted fs-7">{reviews.length} of {reviewsTotal} reviews</span>
                            <Paginator
                              page={reviewsPagination.current_page}
                              rows={reviewsPagination.per_page}
                              totalRecords={reviewsTotal}
                              onPageChange={(p) => setReviewsPage(p)}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* ── Timings ── */}
                {tab === 'timings' && (
                  <div>
                    {timingsLoader ? (
                      <div className="text-center py-10"><Loader text="Loading timings…" size={30} /></div>
                    ) : (
                      <div className="partner-info-card">
                        <div className="partner-info-card-header">
                          <h6 className="partner-info-card-title">
                            <i className="bi bi-clock-fill"></i>Business Timings
                          </h6>
                          <button className="btn btn-sm text-white btn-base" onClick={saveTimings} disabled={timingsSaving}>
                            {timingsSaving
                              ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving…</>
                              : <><i className="bi bi-floppy-fill me-1"></i>Save Timings</>}
                          </button>
                        </div>

                        <div className="timing-controls-bar">
                          {/* Slot Duration */}
                          <div className="timing-slot-duration-row">
                            <span className="timing-section-label">
                              <i className="bi bi-hourglass-split"></i>Slot Duration
                            </span>
                            <div className="d-flex align-items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                max="24"
                                className="form-control form-control-sm timing-time-input"
                                style={{ width: 80 }}
                                value={slotDuration}
                                onChange={e => setSlotDuration(Math.max(1, Math.min(24, Number(e.target.value) || 1)))}
                              />
                              <span style={{ fontSize: 13, color: '#4b5563', fontWeight: 500 }}>hrs</span>
                              <span className="timing-hint">
                                Slots will be created every {slotDuration} hour{slotDuration > 1 ? 's' : ''}
                              </span>
                            </div>
                          </div>

                          {/* Apply same timing for all */}
                          <div className="timing-apply-all-row">
                            <span className="timing-section-label">
                              <i className="bi bi-arrow-repeat"></i>Apply same timing for all days
                            </span>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <input
                                type="time"
                                className="form-control form-control-sm timing-time-input"
                                value={applyAllOpen}
                                onChange={e => setApplyAllOpen(e.target.value)}
                              />
                              <span className="timing-separator">to</span>
                              <input
                                type="time"
                                className="form-control form-control-sm timing-time-input"
                                value={applyAllClose}
                                onChange={e => setApplyAllClose(e.target.value)}
                              />
                              <button className="btn btn-sm btn-light-primary" onClick={applyAllTimings}>
                                <i className="bi bi-check2-all me-1"></i>Apply to All
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Days */}
                        <div>
                          <div className="timing-table-header">
                            <span className="timing-col-day">Day</span>
                            <span className="timing-col-status">Status</span>
                            <span className="timing-col-times">Open — Close</span>
                          </div>
                          {timings.map((t, i) => (
                            <div key={t.day} className={`timing-day-row ${t.is_off ? 'timing-day-off' : ''}`}>
                              <div className="timing-col-day">
                                <span className="timing-day-name">{t.day}</span>
                              </div>
                              <div className="timing-col-status">
                                <div className="form-check form-switch mb-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id={`day-toggle-${i}`}
                                    checked={!t.is_off}
                                    onChange={() => updateDay(i, 'is_off', !t.is_off)}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`day-toggle-${i}`}
                                    style={{ fontSize: 12, fontWeight: 600, color: t.is_off ? '#9ca3af' : '#059669' }}
                                  >
                                    {t.is_off ? 'Off' : 'Open'}
                                  </label>
                                </div>
                              </div>
                              <div className="timing-col-times">
                                {t.is_off ? (
                                  <span className="timing-closed-label">Closed all day</span>
                                ) : (
                                  <div className="d-flex align-items-center gap-2">
                                    <input
                                      type="time"
                                      className="form-control form-control-sm timing-time-input"
                                      value={t.open}
                                      onChange={e => updateDay(i, 'open', e.target.value)}
                                    />
                                    <span className="timing-separator">–</span>
                                    <input
                                      type="time"
                                      className="form-control form-control-sm timing-time-input"
                                      value={t.close}
                                      onChange={e => updateDay(i, 'close', e.target.value)}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="timing-footer">
                          <span className="timing-hint">
                            <i className="bi bi-info-circle me-1"></i>
                            In-app slots will be spaced {slotDuration} hour{slotDuration > 1 ? 's' : ''} apart within each day's open hours.
                          </span>
                          <button className="btn btn-sm text-white btn-base" onClick={saveTimings} disabled={timingsSaving}>
                            {timingsSaving
                              ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving…</>
                              : <><i className="bi bi-floppy-fill me-1"></i>Save Timings</>}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Bank Details ── */}
                {tab === 'bank' && (
                  <div>
                    {bankLoader ? (
                      <div className="text-center py-10"><Loader text="Loading bank details…" size={30} /></div>
                    ) : (
                      <div className="partner-info-card">
                        <div className="partner-info-card-header">
                          <h6 className="partner-info-card-title">
                            <i className="bi bi-bank"></i>Bank Details
                          </h6>
                          <button className="btn btn-sm text-white btn-base" onClick={saveBankDetails} disabled={bankSaving}>
                            {bankSaving
                              ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving…</>
                              : <><i className="bi bi-floppy-fill me-1"></i>Save</>}
                          </button>
                        </div>

                        <div style={{ padding: '20px' }}>
                          <div className="row g-4">
                            {[
                              { key: 'account_title',  label: 'Account Title',  placeholder: 'Account Title' },
                              { key: 'account_number', label: 'Account Number', placeholder: 'Account Number' },
                              { key: 'iban',           label: 'IBAN',           placeholder: 'IBAN' },
                              { key: 'bank_name',      label: 'Bank Name',      placeholder: 'Bank Name' },
                              { key: 'city',           label: 'City',           placeholder: 'City' },
                            ].map(({ key, label, placeholder }) => (
                              <div className="col-md-6" key={key}>
                                <label className="form-label fw-semibold fs-7 text-gray-700">{label}</label>
                                <input
                                  type="text"
                                  className="form-control form-control-solid"
                                  placeholder={placeholder}
                                  value={bank[key]}
                                  onChange={(e) => updateBankField(key, e.target.value)}
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="timing-footer">
                          <span className="timing-hint">
                            <i className="bi bi-info-circle me-1"></i>
                            Bank details are used for partner payouts.
                          </span>
                          <button className="btn btn-sm text-white btn-base" onClick={saveBankDetails} disabled={bankSaving}>
                            {bankSaving
                              ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving…</>
                              : <><i className="bi bi-floppy-fill me-1"></i>Save</>}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default PartnerDetails;
